// @src/pre-deployment/src/components/KoreanAssessment.js v1.0
// - Korean ability assessment for pre-deployment environment
// - Handles initial questions and level assessment
// - Integrates with Firebase to store assessment results

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import questionsData from '../../data/questions.json';

const KoreanAssessment = () => {
  const [currentStep, setCurrentStep] = useState('initial'); // initial, hangul, assessment
  const [initialAnswers, setInitialAnswers] = useState({});
  const [currentLevel, setCurrentLevel] = useState(1);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [assessmentAnswers, setAssessmentAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { completeAssessment, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    console.group('📝 PRE-DEPLOYMENT KOREAN ASSESSMENT: Component mounted');
    console.log('Current user:', user?.uid);
    console.log('Questions data loaded:', !!questionsData);
    console.groupEnd();

    // Redirect if no authenticated user
    if (!user) {
      console.log('No authenticated user, redirecting to home');
      navigate('/');
    }
  }, [user, navigate]);

  // FIX-069: Hoisted above selectInitialOption/nextQuestion for no-use-before-define
  const finishAssessment = async (allAnswers, predefinedData = null) => {
    console.group('📝 PRE-DEPLOYMENT KOREAN ASSESSMENT: Finishing assessment');
    setIsLoading(true);

    let assessmentData;

    if (predefinedData) {
      assessmentData = predefinedData;
    } else {
      // Calculate final level based on smart testing performance
      const levelScores = {};
      let finalLevel = 0;

      for (let lvl = 1; lvl <= 6; lvl++) {
        const levelAnswers = allAnswers.filter(a => a.level === lvl);
        if (levelAnswers.length > 0) {
          const correct = levelAnswers.filter(a => a.isCorrect).length;
          levelScores[lvl] = (correct / levelAnswers.length) * 100;
        }
      }

      const attemptedLevels = Object.keys(levelScores).map(Number).sort((a, b) => a - b);

      if (attemptedLevels.length === 0) {
        finalLevel = 0;
      } else {
        finalLevel = 0;
        for (const lvl of attemptedLevels) {
          const levelAnswers = allAnswers.filter(a => a.level === lvl);
          if (levelAnswers.length >= 2) {
            const q1Correct = levelAnswers[0].isCorrect;
            const q2Correct = levelAnswers[1].isCorrect;
            if (q1Correct && q2Correct) {
              finalLevel = lvl;
            } else if (!q1Correct && !q2Correct) {
              finalLevel = lvl;
              break;
            } else if (levelAnswers.length === 3) {
              const q3Correct = levelAnswers[2].isCorrect;
              if (q3Correct) {
                finalLevel = lvl;
              } else {
                finalLevel = lvl;
                break;
              }
            } else {
              finalLevel = lvl;
              break;
            }
          }
        }
        if (finalLevel === 0 && attemptedLevels.length > 0) {
          finalLevel = attemptedLevels[attemptedLevels.length - 1];
        }
      }

      assessmentData = {
        initialAnswers,
        levelScores,
        finalLevel,
        allAnswers,
        completedAt: new Date().toISOString(),
        totalQuestions: allAnswers.length
      };
    }

    try {
      if (completeAssessment && user?.uid) {
        await completeAssessment(assessmentData);
      }
      setCurrentStep('complete');
      navigate('/level/0/chapter/1/complete', { state: { assessmentData } });
    } catch (err) {
      console.error('Assessment completion error:', err);
    } finally {
      setIsLoading(false);
      console.groupEnd();
    }
  };

  const moveToNextLevel = (allAnswers) => {
    if (currentLevel < 6) {
      console.log(`📝 Moving from Level ${currentLevel} to Level ${currentLevel + 1}`);
      setCurrentLevel(currentLevel + 1);
      setCurrentQuestionIndex(0);
      setMessage(`Great! Moving to Level ${currentLevel + 1}`);
    } else {
      console.log('📝 Reached maximum level (6) - assessment complete');
      finishAssessment(allAnswers);
    }
  };

  const selectInitialOption = (question, answer) => {
    console.log(`📝 PRE-DEPLOYMENT KOREAN ASSESSMENT: Initial question - ${question}: ${answer}`);
    
    const newAnswers = { ...initialAnswers, [question]: answer };
    setInitialAnswers(newAnswers);

    if (question === 'korean') {
      if (answer === 'no') {
        // If never taken Korean, show hangul question
        setCurrentStep('hangul');
      } else {
        // If taken Korean before, start assessment at Level 1
        setCurrentStep('assessment');
        setCurrentLevel(1); // Start at level 1 for those with experience
      }
    } else if (question === 'hangul') {
      if (answer === 'no') {
        // If don't know hangul, complete assessment with level 0 and go to complete page
        console.log('📝 PRE-DEPLOYMENT KOREAN ASSESSMENT: User does not know hangul - completing with level 0');
        const assessmentData = {
          initialAnswers: { ...newAnswers },
          levelScores: { 0: 0 },
          finalLevel: 0,
          allAnswers: [],
          completedAt: new Date().toISOString(),
          totalQuestions: 0
        };
        finishAssessment([], assessmentData);
        return;
      } else {
        // If know hangul, start at Level 1
        setCurrentLevel(1);
      }
      setCurrentStep('assessment');
    }
  };

  const getCurrentQuestions = () => {
    const levelKey = `level${currentLevel}`;
    return questionsData[levelKey] || [];
  };

  const selectAnswer = (answer) => {
    console.log(`📝 PRE-DEPLOYMENT KOREAN ASSESSMENT: Selected answer: ${answer}`);
    setSelectedAnswer(answer);
  };

  const nextQuestion = () => {
    if (!selectedAnswer) {
      setMessage('Please select an answer before continuing.');
      return;
    }

    const questions = getCurrentQuestions();
    const currentQuestion = questions[currentQuestionIndex];
    
    // Store the answer
    const answerData = {
      level: currentLevel,
      questionIndex: currentQuestionIndex,
      questionId: currentQuestion.id,
      selectedAnswer,
      correctAnswer: currentQuestion.correct,
      isCorrect: selectedAnswer === currentQuestion.correct
    };
    
    const newAnswers = [...assessmentAnswers, answerData];
    setAssessmentAnswers(newAnswers);
    
    console.log('📝 PRE-DEPLOYMENT KOREAN ASSESSMENT: Answer recorded:', answerData);
    
    // Clear selection
    setSelectedAnswer('');
    setMessage('');
    
    // Smart testing logic
    const currentLevelAnswers = newAnswers.filter(a => a.level === currentLevel);
    const currentQuestionNumber = currentQuestionIndex + 1; // 1-based
    
    console.log(`📝 Current question: ${currentQuestionNumber}/3 for Level ${currentLevel}`);
    
    if (currentQuestionNumber === 1) {
      // Just finished Q1, move to Q2
      setCurrentQuestionIndex(1);
      console.log('Moving to Question 2');
    } else if (currentQuestionNumber === 2) {
      // Just finished Q2, check if we need Q3
      const q1Correct = currentLevelAnswers[0].isCorrect;
      const q2Correct = currentLevelAnswers[1].isCorrect;
      
      console.log(`Q1: ${q1Correct ? '✅' : '❌'}, Q2: ${q2Correct ? '✅' : '❌'}`);
      
      if (q1Correct && q2Correct) {
        // Both correct - skip Q3, move to next level
        console.log('📝 Both Q1 and Q2 correct - skipping Q3, moving to next level');
        moveToNextLevel(newAnswers);
      } else if (!q1Correct && !q2Correct) {
        // Both wrong - skip Q3, current level is final
        console.log('📝 Both Q1 and Q2 wrong - skipping Q3, assessment complete');
        finishAssessment(newAnswers);
      } else {
        // One correct, one wrong - need Q3
        console.log('📝 Q1 and Q2 mixed results - moving to Q3 for final decision');
        setCurrentQuestionIndex(2);
        setMessage('Final question for this level...');
      }
    } else if (currentQuestionNumber === 3) {
      // Just finished Q3 (tie-breaker question)
      const q3Correct = answerData.isCorrect;
      
      console.log(`Q3 (tie-breaker): ${q3Correct ? '✅' : '❌'}`);
      
      if (q3Correct) {
        // Q3 correct - move to next level
        console.log('📝 Q3 correct - moving to next level');
        moveToNextLevel(newAnswers);
      } else {
        // Q3 wrong - current level is final
        console.log('📝 Q3 wrong - assessment complete');
        finishAssessment(newAnswers);
      }
    }
  };

  const goBack = () => {
    if (currentStep === 'hangul') {
      setCurrentStep('initial');
      setInitialAnswers({});
    } else if (currentStep === 'assessment') {
      if (initialAnswers.korean === 'no') {
        setCurrentStep('hangul');
      } else {
        setCurrentStep('initial');
      }
    } else {
      navigate('/setup-password');
    }
  };

  const renderInitialQuestions = () => (
    <div className="question-container">
      <h3>1. Have you taken Korean before?</h3>
      <div className="option-buttons">
        <button 
          className={`option-btn ${initialAnswers.korean === 'yes' ? 'selected' : ''}`}
          onClick={() => selectInitialOption('korean', 'yes')}
        >
          Yes
        </button>
        <button 
          className={`option-btn ${initialAnswers.korean === 'no' ? 'selected' : ''}`}
          onClick={() => selectInitialOption('korean', 'no')}
        >
          No
        </button>
      </div>
    </div>
  );

  const renderHangulQuestion = () => (
    <div className="question-container">
      <h3>2. Do you know 한글?</h3>
      <div className="option-buttons">
        <button 
          className={`option-btn ${initialAnswers.hangul === 'yes' ? 'selected' : ''}`}
          onClick={() => selectInitialOption('hangul', 'yes')}
        >
          Yes
        </button>
        <button 
          className={`option-btn ${initialAnswers.hangul === 'no' ? 'selected' : ''}`}
          onClick={() => selectInitialOption('hangul', 'no')}
        >
          No
        </button>
      </div>
    </div>
  );

  const renderAssessmentQuestion = () => {
    const questions = getCurrentQuestions();
    if (!questions.length) return null;
    
    const currentQuestion = questions[currentQuestionIndex];
    // Dynamic progress calculation - we don't know if we'll need Q3 until after Q2
    let maxQuestions = 3;
    let currentQuestionNumber = currentQuestionIndex + 1;
    
    // If we're on Q3, we know we needed all 3 questions
    // If we're on Q1 or Q2, we might need 2 or 3 questions
    if (currentQuestionNumber <= 2) {
      maxQuestions = 2; // Show progress as if we only need 2 questions initially
    }
    
    const progress = (currentQuestionNumber / maxQuestions) * 100;
    
    return (
      <div className="question-container">
        <div className="level-indicator">
          <h3>Level {currentLevel} Assessment</h3>
          <p>Question {currentQuestionNumber}{currentQuestionNumber <= 2 ? ' of 2-3' : ' of 3'} (Smart Testing)</p>
        </div>
        
        <div className="question-content">
          <h4>{currentQuestion.question}</h4>
          <div className="option-buttons">
            {currentQuestion.options.map((option, index) => (
              <button
                key={index}
                className={`option-btn ${selectedAnswer === option ? 'selected' : ''}`}
                onClick={() => selectAnswer(option)}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
        
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }}></div>
        </div>
        
        <button 
          className="continue-btn" 
          onClick={nextQuestion}
          disabled={!selectedAnswer || isLoading}
        >
          {isLoading ? 'Processing...' : 'Continue'}
        </button>
      </div>
    );
  };

  return (
    <div className="container">
      <div className="logo-section">
        <img src="/img/logo.png" alt="Logo" className="logo" />
        <h1>Pre-Deployment</h1>
        <p>Korean Assessment</p>
      </div>
      
      <div className="auth-section">
        <div className="navigation-controls">
          <button className="nav-btn secondary" onClick={goBack}>
            <i className="fas fa-arrow-left"></i>
            Back
          </button>
        </div>

        <h2>Korean Level Assessment</h2>
        
        {currentStep === 'initial' && renderInitialQuestions()}
        {currentStep === 'hangul' && renderHangulQuestion()}
        {currentStep === 'assessment' && renderAssessmentQuestion()}

        {message && (
          <div className={`message ${message.includes('Great') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default KoreanAssessment;
